import { ChatConversationSummaryDto, JobDto } from 'src/api/FiretrolApiClient';
import { stripMemoryMarkers } from 'src/app/chat/memoryMarkers';
import { segmentToMarkdown } from 'src/app/chat/segmentKinds';
import { IChatUiMessage } from 'src/hooks/useChat';

export const UNTITLED_CONVERSATION_TITLE = 'New F.I.R.E. Chat';

export interface IChatTranscript {
  title: string;
  jobLabel?: string;
  startedAt?: Date;
  exportedAt: Date;
  messages: IChatUiMessage[];
}

export interface ITranscriptFormatOptions {
  locale?: string;
  timeZone?: string;
  /** Origin that `ref:` entity links resolve against; without it they export as their labels. */
  appOrigin?: string;
}

const ROLE_LABELS: Record<string, string> = {
  user: 'You',
  assistant: 'F.I.R.E.',
};

export const transcriptRoleLabel = (role: string): string =>
  ROLE_LABELS[role] ?? role.charAt(0).toUpperCase() + role.slice(1);

export const formatTranscriptTimestamp = (
  date: Date,
  options: ITranscriptFormatOptions = {},
): string =>
  new Intl.DateTimeFormat(options.locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: options.timeZone,
  }).format(date);

const hasAnswer = (message: IChatUiMessage): boolean =>
  message.segments.some(
    (segment) =>
      (segment.kind === 'text' &&
        stripMemoryMarkers(segment.text).trim().length > 0) ||
      segment.kind === 'chart' ||
      segment.kind === 'table',
  );

/** Why an assistant reply is incomplete, or null when it finished normally. */
export const describeReplyStatus = (message: IChatUiMessage): string | null => {
  if (message.role !== 'assistant') return null;
  if (message.interruption === 'stopped') {
    return 'This reply was stopped before it finished.';
  }
  if (message.interruption === 'failed' || message.stopReason === 'error') {
    return 'This reply failed before it finished.';
  }
  if (message.streaming) return 'This reply was still being written.';
  switch (message.stopReason) {
    case 'max_tokens':
      return 'This reply was cut short because it reached its length limit.';
    case 'max_tool_iterations':
      return 'This reply reached the tool limit before it finished.';
    case 'incomplete':
      return 'This reply did not complete.';
  }
  return hasAnswer(message) ? null : 'No answer was written for this reply.';
};

const messageToMarkdown = (
  message: IChatUiMessage,
  options: ITranscriptFormatOptions,
): string => {
  const parts: string[] = [
    `### ${transcriptRoleLabel(message.role)} · ${formatTranscriptTimestamp(
      message.createdAt,
      options,
    )}`,
  ];
  for (const segment of message.segments) {
    const markdown = segmentToMarkdown(segment, {
      role: message.role,
      options,
    });
    if (markdown !== null) parts.push(markdown);
  }
  const status = describeReplyStatus(message);
  if (status) parts.push(`> _${status}_`);
  return parts.join('\n\n');
};

/** @registry Pure formatter that renders a chat conversation (title, job, per-message role/time, query names with row counts, charts and tables as GFM tables, memory notes as one line each, incomplete-reply notes) as a Markdown document; assistant markdown and user text pass through unchanged, except that assistant `[memory:N]` markers are removed and assistant and table entity links become absolute app URLs with `appOrigin` and plain labels without it. */
export const buildTranscriptMarkdown = (
  transcript: IChatTranscript,
  options: ITranscriptFormatOptions = {},
): string => {
  const meta = [
    `- **Exported:** ${formatTranscriptTimestamp(
      transcript.exportedAt,
      options,
    )}`,
  ];
  if (transcript.startedAt) {
    meta.push(
      `- **Started:** ${formatTranscriptTimestamp(
        transcript.startedAt,
        options,
      )}`,
    );
  }
  if (transcript.jobLabel) meta.push(`- **Job:** ${transcript.jobLabel}`);

  const header = `# ${transcript.title}\n\n${meta.join('\n')}`;
  const body = transcript.messages.map((message) =>
    messageToMarkdown(message, options),
  );
  return `${[header, ...body].join('\n\n---\n\n')}\n`;
};

const slugify = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');

/** File name stem shared by the Markdown download and the print document title. */
export const transcriptFileStem = (
  title: string,
  exportedAt: Date,
  options: Pick<ITranscriptFormatOptions, 'timeZone'> = {},
): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: options.timeZone,
  }).formatToParts(exportedAt);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const date = `${part('year')}-${part('month')}-${part('day')}`;
  return `${slugify(title) || 'fire-chat'}-${date}`;
};

/** @registry Builds the export model for a chat conversation: its title (or the untitled default), job label, start time, and messages, stamped with the export time. */
export const buildChatTranscript = (
  conversation: ChatConversationSummaryDto | undefined,
  jobs: JobDto[],
  messages: IChatUiMessage[],
  exportedAt: Date = new Date(),
): IChatTranscript => {
  // The generated DTO types jobId as optional, but the API sends null for a conversation with no job.
  const jobId = conversation?.jobId ?? undefined;
  const job =
    jobId === undefined ? undefined : jobs.find((j) => j.id === jobId);
  return {
    title: conversation?.title || UNTITLED_CONVERSATION_TITLE,
    jobLabel: job
      ? `${job.jobNumber} - ${job.name}`
      : jobId !== undefined
      ? `Job ${jobId}`
      : undefined,
    startedAt: conversation && new Date(conversation.createdAt),
    exportedAt,
    messages,
  };
};
